import { useMutation } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { Button } from 'src/components/button'
import { ErrorMessage, ErrorsList } from 'src/components/errors'
import { Input } from 'src/components/input'
import {
  LoginForm,
  LoginFormInputs,
  LoginShape,
  TLoginForm,
  userApi,
  UserWithToken,
} from 'src/services/user'

import { PasswordInput } from 'src/components/password-input'
import { useAuth } from 'src/stores/auth'
import { getOAuthAuthorizeNext, useFollowupRoute } from 'src/utils/auth'
import { getErrorMessages } from 'src/utils/errors'
import Logo from '../assets/images/logo.png'
import { useTranslation } from 'react-i18next'
import { LegalFooterLinks } from 'src/components/legal-footer-links'

function LogInInner() {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState<string[] | undefined>()
  const { changeToken, changeUserId, writeUserInStorage } = useAuth.use.actions()
  const { createFormFieldChangeHandler, form } = useTnForm<TLoginForm>()
  const navigate = useNavigate()

  const { mutate: logIn, isPending } = useMutation({
    mutationFn: async (loginData: LoginShape) => await userApi.csc.login(loginData),
    onSuccess: (data: UserWithToken) => {
      changeToken(data.token)
      changeUserId(data.id)
      // Store memberships now: the route guards check permissions on the very next
      // render, before the user query in the layout has loaded them.
      writeUserInStorage(data)
      // Login also establishes the Django session the OAuth consent page needs.
      const oauthNext = getOAuthAuthorizeNext()
      if (oauthNext) {
        window.location.assign(oauthNext)
        return
      }
      navigate(`/reservations`)
    },
    onError(e: any) {
      const errors = getErrorMessages(e)
      setErrorMessage(errors)
    },
  })

  const handleLogin = () => {
    const input = {
      email: form.email.value ?? '',
      password: form.password.value ?? '',
    }
    logIn(input)
  }

  const token = useAuth.use.token()
  const isAuth = Boolean(token)
  const followupRoute = useFollowupRoute()
  // An OAuth authorize redirect needs a Django session, which the stored token
  // doesn't give, so show the form even when already signed in.
  if (isAuth && !getOAuthAuthorizeNext()) {
    const state = { from: followupRoute }
    return <Navigate to={'/'} state={state} />
  }

  return (
    <div className="flex min-h-screen flex-col sm:flex-row">
      {/* Left Section (Alternate Background) */}
      <div className="hidden items-center justify-center bg-surface-hover sm:flex sm:w-1/2">
        <img className="h-[20rem] w-auto" src={Logo} alt="RAPA-HOST" />
      </div>

      {/* Right Section (Login Form) */}
      <div className="flex flex-1 flex-col justify-center bg-surface-hover px-6 py-12 sm:w-1/2 sm:bg-surface sm:px-8">
        {/* Logo for mobile */}
        <div className="mx-auto sm:hidden">
          <img className="h-[10rem] w-auto" src={Logo} alt="RAPA-HOST" />
        </div>

        {/* Login Form */}
        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleLogin()
            }}
          >
            <Input
              placeholder={t('auth.emailPlaceholder')}
              onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
              value={form.email.value ?? ''}
              data-testid="email"
              id="id"
              label={t('auth.emailLabel')}
              type="email"
              autoComplete="email"
            />
            <ErrorsList errors={form.email.errors} />
            <div className="mt-2">
              <PasswordInput
                placeholder={t('auth.passwordPlaceholder')}
                onChange={(e) => {
                  createFormFieldChangeHandler(form.password)(e.target.value)
                }}
                value={form.password.value ?? ''}
                data-testid="password"
                id="password"
              />
              <div className="flex w-full items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-primary"
                >
                  {t('auth.passwordLabel')}
                </label>
                <div className="text-sm hover:underline">
                  <Link to="/request-reset" className="font-semibold text-accent">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>
              <ErrorsList errors={form.password.errors} />
            </div>
            <div className="mb-2">
              <ErrorMessage>{errorMessage}</ErrorMessage>
            </div>
            {!isPending ? (
              <Button
                data-testid="submit"
                type="submit"
                variant="primary"
                disabled={isPending || !form.isValid}
                className="mt-2 w-full"
              >
                {t('auth.login')}
              </Button>
            ) : (
              <div className="mt-2 flex justify-center">
                <span>{t('auth.loggingIn')}</span>
              </div>
            )}
          </form>
        </div>
        {/* Footer */}
        <div className="m-4 flex self-center text-sm">
          <p className="mr-2">{t('auth.dontHaveAccount')}</p>
          <Link className="font-bold text-primary hover:underline" to="/sign-up">
            {t('auth.signUpLink')}
          </Link>
        </div>
        <LegalFooterLinks />
      </div>
    </div>
  )
}

export const LogIn = () => {
  return (
    <FormProvider<LoginFormInputs> formClass={LoginForm}>
      <LogInInner />
    </FormProvider>
  )
}
